import { Fragment, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PreviewService } from 'core/api/services/preview';
import { useExperiences } from 'core/contexts/experienceV2';
import { useTranslation } from 'react-i18next';
import { ExperienceStatus, formatCurrency, timeDurationFormatter } from '@utriper/core';
import { Box, Button, Fade, Grid2, IconButton, Tooltip, Typography } from '@mui/material';
import { BasicTable, CheckboxComponent, DropdownCheckbox, PaginationControlled, PreviewPriceGroup } from '@utriper/core-ui';
import Placeholder from 'assets/images/placeholder.webp';
import { Edit, Inventory2Outlined, TaskAlt } from '@mui/icons-material';
import UpdateStatusForm from 'modules/Experience/components/UpdateStatusForm';
import ExperienceStatusChip from 'components/ExperienceStatusChip';

const ExperienceTable = () => {
    const { t, i18n } = useTranslation();
    const translate = (key) => t(`experience_list.table.${key}`);
    const history = useHistory();
    const { loading, experiences, totalExperiences, MAX_PAGE, expPage, handlePage, handleExperience } = useExperiences();
    const [experiencesSelected, setExperiencesSelected] = useState([]);
    const [activeOption, setActiveOption] = useState('all');
    const [checkedOption, setCheckedOption] = useState(false);
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(null);
    const [experienceId, setExperienceId] = useState(null);

    const ALL_OPTIONS = 'all';
    const NONE_OPTIONS = 'none';
    const dropdownOptions = useMemo(
        () => [
            {
                id: ALL_OPTIONS,
                label: translate('select_options.all'),
            },
            {
                id: NONE_OPTIONS,
                label: translate('select_options.none'),
            },
            ...ExperienceStatus.getIds().map((status) => ({
                id: status,
                label: ExperienceStatus.getTranslatedName(ExperienceStatus.status, status, i18n.language),
            })),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [i18n.language]
    );

    const handleOptionChange = (optionId) => {
        setActiveOption(optionId);
        if (activeOption !== optionId && checkedOption) {
            setCheckedOption(false);
            setExperiencesSelected([]);
        }
    };

    const handleCheckAll = (newChecked) => {
        if (!Number.isInteger(activeOption) && activeOption !== ALL_OPTIONS && activeOption !== NONE_OPTIONS) return;

        if (newChecked === false) {
            setExperiencesSelected([]);
            setCheckedOption(false);
            return;
        }

        if (activeOption === NONE_OPTIONS) {
            setExperiencesSelected([]);
            setCheckedOption(true);
            return;
        }

        if (activeOption === ALL_OPTIONS) {
            setExperiencesSelected([...experiences.map((exp) => exp.id)]);
            setCheckedOption(true);
            return;
        }

        if (Number.isInteger(activeOption)) {
            const filteredExperiences = experiences.filter((exp) => exp?.status === activeOption);
            setExperiencesSelected([...filteredExperiences.map((exp) => exp.id)]);
            setCheckedOption(true);
            return;
        }
    };

    const align = 'left';
    const experienceColumns = [
        {
            align: 'center',
            val: 'checked',
            label: <DropdownCheckbox options={dropdownOptions} value={activeOption} checked={checkedOption} onOptionChange={handleOptionChange} onCheckboxChange={handleCheckAll} />,
            style: { width: '48px !important', boxSizing: 'border-box' },
        },
        {
            align,
            val: 'experienceData',
            label: translate('columns.experience'),
            style: { width: '320px' },
        },
        {
            align,
            val: 'experienceLocation',
            label: translate('columns.location'),
            style: { width: '180px' },
        },
        {
            align,
            val: 'experiencePrice',
            label: translate('columns.price'),
            style: { width: '180px' },
        },
        {
            align,
            val: 'experienceDuration',
            label: translate('columns.duration'),
            style: { width: '180px' },
        },
        {
            align,
            val: 'experienceStatus',
            label: translate('columns.status'),
            style: { width: '160px' },
        },
        {
            align,
            val: 'actions',
            label: translate('columns.actions'),
            style: { width: '100px' },
        },
    ];

    const handleCheck = (experienceId, isChecked) => {
        if (isChecked) {
            const filteredExperiences = experiencesSelected?.filter((exp) => exp !== experienceId);

            if (filteredExperiences?.length <= 0) {
                setCheckedOption(false);
                setActiveOption(ALL_OPTIONS);
            }

            setExperiencesSelected([...filteredExperiences]);
            return;
        }

        if (experiencesSelected?.length <= 0) {
            setCheckedOption(true);
            setActiveOption(ALL_OPTIONS);
        }

        setExperiencesSelected((prev) => [...prev, experienceId]);
    };

    const prettifyExperiences = () =>
        experiences?.map(({ id, name, description1, status, location, price, priceGroupId, currency, duration, pictureURL }) => {
            const imageSrc = pictureURL ? `${process.env.REACT_APP_CDN_URL}${pictureURL}` : Placeholder;
            const checked = experiencesSelected.find((exp) => exp === id) ?? false;

            return {
                id,
                checked: (
                    <Box sx={{ '& .MuiFormControl-root': { width: 'fit-content' } }}>
                        <CheckboxComponent id="select-experience" sx={{ margin: 0, width: 'fit-content' }} checked={checked} onChange={() => handleCheck(id, checked)} />
                    </Box>
                ),
                experienceData: (
                    <Grid2 container size={12} alignItems="center">
                        <Grid2 container size={3}>
                            <Box
                                component="img"
                                src={imageSrc}
                                alt={`${name}-image`}
                                sx={{
                                    aspectRatio: '1 / 1',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    width: 'auto',
                                    height: '64px',
                                    borderRadius: '8px',
                                }}
                            />
                        </Grid2>
                        <Grid2
                            container
                            size={9}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            direction="column"
                            spacing={1}
                        >
                            <Typography fontSize={18} fontWeight={700} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                                {name}
                            </Typography>
                            <Typography fontSize={14} fontWeight={500} sx={{ color: '#131313bf', maxWidth: '100%' }} noWrap>
                                {description1}
                            </Typography>
                        </Grid2>
                    </Grid2>
                ),
                experienceLocation: (
                    <Typography fontSize={14} fontWeight={500} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                        {location}
                    </Typography>
                ),
                experiencePrice: priceGroupId ? (
                    <PreviewPriceGroup id={priceGroupId} label="name" previewService={PreviewService} styles={{ fontSize: '14px', color: '#131313', maxWidth: '100%', whiteSpace: 'break-spaces' }} route={`/price-group/${priceGroupId}`} />
                ) : (
                    <Typography fontSize={14} fontWeight={500} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                        {Number.isFinite(price) && currency ? formatCurrency(price, currency, i18n.language) : ''}
                    </Typography>
                ),
                experienceDuration: (
                    <Typography fontSize={14} fontWeight={500} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                        {timeDurationFormatter(duration, t)}
                    </Typography>
                ),
                experienceStatus: <ExperienceStatusChip status={status} />,
                actions: (
                    <Grid2 container width="fit-content" justifyContent="space-between" alignItems="center" gap="8px">
                        <Tooltip title={t('common.edit')} placement="top">
                            <IconButton onClick={() => history.push(`/experience/${id}/edit`)} sx={{ color: '#131313' }}>
                                <Edit />
                            </IconButton>
                        </Tooltip>
                        {status !== ExperienceStatus.IDS.live ? (
                            <Tooltip title={t('common.publish')} placement="top">
                                <IconButton
                                    onClick={() => {
                                        setStatus(ExperienceStatus.IDS.live);
                                        setExperienceId(id);
                                        setOpen(true);
                                    }}
                                    sx={{ color: '#50C878' }}
                                >
                                    <TaskAlt />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title={t('common.to_archive')} placement="top">
                                <IconButton
                                    onClick={() => {
                                        setStatus(ExperienceStatus.IDS.archived);
                                        setExperienceId(id);
                                        setOpen(true);
                                    }}
                                    sx={{ color: '#E4954B' }}
                                >
                                    <Inventory2Outlined />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Grid2>
                ),
            };
        });

    return (
        <Fragment>
            <Fade in={!loading} timeout={300}>
                <Box component="section" display="flex" flexDirection="column" mt="24px" gap="24px">
                    {experiencesSelected?.length > 0 && (
                        <Grid2
                            container
                            size={12}
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                backgroundColor: 'background.paper',
                                py: 2,
                            }}
                        >
                            <Box display="flex" flexWrap="nowrap" alignItems="center" gap="24px" maxWidth="100%" overflow="auto" px={2}>
                                <Typography fontSize={18} fontWeight={600} sx={{ color: '#131313', minWidth: 'fit-content' }}>{`${experiencesSelected.length} ${translate('selected')}`}</Typography>
                                {(activeOption === ALL_OPTIONS || activeOption === ExperienceStatus.IDS.draft || activeOption === ExperienceStatus.IDS.archived) && (
                                    <Button
                                        variant="text"
                                        sx={{ textTransform: 'none', fontWeight: 600, color: '#131313', minWidth: 'fit-content' }}
                                        startIcon={<TaskAlt />}
                                        onClick={() => {
                                            setStatus(ExperienceStatus.IDS.live);
                                            setOpen(true);
                                        }}
                                    >
                                        {t('common.publish')}
                                    </Button>
                                )}
                                {(activeOption === ALL_OPTIONS || activeOption === ExperienceStatus.IDS.live) && (
                                    <Button
                                        variant="text"
                                        sx={{ textTransform: 'none', fontWeight: 600, color: '#131313', minWidth: 'fit-content' }}
                                        startIcon={<Inventory2Outlined />}
                                        onClick={() => {
                                            setStatus(ExperienceStatus.IDS.archived);
                                            setOpen(true);
                                        }}
                                    >
                                        {t('common.to_archive')}
                                    </Button>
                                )}
                            </Box>
                        </Grid2>
                    )}
                    <Grid2 container size={12} spacing={2}>
                        <BasicTable size="small" columns={experienceColumns} values={prettifyExperiences()} onClick={(e, experienceId) => handleExperience(experienceId, history)} />
                    </Grid2>
                    {totalExperiences / MAX_PAGE > 1 && (
                        <PaginationControlled
                            count={Math.ceil(totalExperiences / MAX_PAGE)}
                            page={expPage}
                            onChange={(event, newPage) => {
                                handlePage(event, newPage);
                                setExperiencesSelected([]);
                                setActiveOption(null);
                            }}
                            totalItems={totalExperiences}
                            itemsPerPage={MAX_PAGE}
                        />
                    )}
                </Box>
            </Fade>

            {open && (
                <UpdateStatusForm
                    open={open}
                    handleClose={() => {
                        setOpen(false);
                        setExperienceId(null);
                        setExperiencesSelected([]);
                        setCheckedOption(false);
                        setActiveOption(ALL_OPTIONS);
                        setStatus(null);
                    }}
                    status={status}
                    experienceId={experienceId}
                    experienceIds={experiencesSelected}
                />
            )}
        </Fragment>
    );
};

export default ExperienceTable;
